"use client";

import React, { createContext, useContext } from "react";
import type { ComposeWatchedFieldsContextValue } from "@/lib/core/crm/types";

/**
 * The volatile half of the compose form state: the `form.watch(...)` subject and
 * body, which change on EVERY keystroke.
 *
 * Kept deliberately separate from `ComposeFormStateContext` (the stable half) so
 * that consumers which never read the typed text — the tab strip, the form
 * chrome — are not re-subscribed on every character. Only the two components
 * that genuinely re-derive from the text (`VariableCounter`,
 * `ContextVariablesList`) subscribe here.
 */
const ComposeWatchedFieldsContext = createContext<ComposeWatchedFieldsContextValue | null>(
  null
);

interface ComposeWatchedFieldsProviderProps {
  children: React.ReactNode;
  value: ComposeWatchedFieldsContextValue;
}

export function ComposeWatchedFieldsProvider({
  children,
  value,
}: ComposeWatchedFieldsProviderProps) {
  return (
    <ComposeWatchedFieldsContext.Provider value={value}>
      {children}
    </ComposeWatchedFieldsContext.Provider>
  );
}

export function useComposeWatchedFieldsContext(): ComposeWatchedFieldsContextValue {
  const context = useContext(ComposeWatchedFieldsContext);
  if (!context) {
    throw new Error(
      "useComposeWatchedFieldsContext must be used within a ComposeWatchedFieldsProvider"
    );
  }
  return context;
}
